import { useState } from 'react';

const areas = [
  { name: '岡山市', detail: '北区・中区・南区・東区', highlight: true },
  { name: '倉敷市', detail: '倉敷・児島・玉島・水島エリア', highlight: true },
  { name: '玉野市', detail: '全域対応', highlight: false },
  { name: '総社市', detail: '全域対応', highlight: false },
  { name: '浅口市', detail: '全域対応', highlight: false },
  { name: '早島町', detail: '全域対応', highlight: false },
  { name: '里庄町', detail: '全域対応', highlight: false },
  { name: 'その他', detail: 'まずはご相談ください', highlight: false },
];

const stats = [
  { icon: 'ri-map-2-line', num: '7市町', label: '対応エリア' },
  { icon: 'ri-car-line', num: '30分', label: '最短訪問' },
  { icon: 'ri-home-4-line', num: '500件+', label: '累計対応' },
];

export default function ServiceAreaSection() {
  const [hovered, setHovered] = useState<string | null>(null);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="area" aria-label="対応エリアのご案内" className="py-20 md:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block text-amber-700 text-sm font-semibold tracking-widest mb-3 uppercase">
            対応エリア
          </span>
          <h2
            className="text-2xl md:text-4xl font-black text-stone-800 leading-tight"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            岡山県南部を中心に<br />地域密着でお伺いします
          </h2>
          <p className="mt-4 text-stone-500 text-sm md:text-base max-w-xl mx-auto">
            岡山市・倉敷市を中心に、岡山県南部全域に対応しています。エリア外の方もまずはご相談ください。
          </p>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap justify-center gap-6 md:gap-12 mb-12">
          {stats.map((s) => (
            <div key={s.label} className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-full">
                <i className={`${s.icon} text-amber-600 text-lg`}></i>
              </div>
              <div>
                <p className="text-stone-800 font-black text-xl" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  {s.num}
                </p>
                <p className="text-stone-400 text-xs">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Map + Area List */}
        <div className="flex flex-col lg:flex-row gap-8 items-stretch">
          {/* Google Map */}
          <div className="w-full lg:w-2/3 rounded-2xl overflow-hidden border border-stone-100 min-h-[360px] md:min-h-[440px]">
            <iframe
              title="対応エリアマップ"
              src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d104720.5!2d133.9!3d34.65!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sja!2sjp!4v1700000000000!5m2!1sja!2sjp"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '360px' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>

          {/* Area List */}
          <div className="w-full lg:w-1/3 flex flex-col gap-3">
            <p className="text-stone-500 text-xs font-semibold tracking-widest uppercase mb-1">
              対応市町村一覧
            </p>
            {areas.map((area) => (
              <div
                key={area.name}
                onMouseEnter={() => setHovered(area.name)}
                onMouseLeave={() => setHovered(null)}
                className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all cursor-default ${
                  area.highlight
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-stone-50 border-stone-100'
                } ${hovered === area.name ? 'border-amber-300 bg-amber-50' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full shrink-0 ${area.highlight ? 'bg-amber-500' : 'bg-stone-300'}`}></div>
                  <div>
                    <p className={`font-bold text-sm ${area.highlight ? 'text-stone-800' : 'text-stone-600'}`}>
                      {area.name}
                    </p>
                    <p className="text-stone-400 text-xs">{area.detail}</p>
                  </div>
                </div>
                {area.highlight && (
                  <span className="text-amber-600 text-xs font-semibold bg-amber-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                    主要エリア
                  </span>
                )}
              </div>
            ))}

            {/* CTA */}
            <div className="mt-2 bg-stone-800 rounded-xl p-4 text-center">
              <p className="text-white text-sm font-semibold mb-1" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                エリア外の方もご相談を
              </p>
              <p className="text-stone-400 text-xs mb-3">
                記載外のエリアも対応できる場合があります
              </p>
              <button
                onClick={() => scrollTo('contact')}
                className="cursor-pointer whitespace-nowrap w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-2.5 rounded-full text-sm transition-colors flex items-center justify-center gap-2"
              >
                <i className="ri-phone-line"></i>
                エリア確認・無料相談
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
